import { useCallback, useEffect, useRef, useState } from "react";
import { LogOut, Globe } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { API, mergeContent } from "@/lib/site";
import { adminFetch, setToken, getToken, formatApiErrorDetail } from "./adminApi";
import AdminDashboard from "./sections/AdminDashboard";
import AdminInquiries from "./sections/AdminInquiries";
import PackagesTab from "./sections/PackagesTab";
import AddonsTab from "./sections/AddonsTab";
import TextsTab from "./sections/TextsTab";
import AdminSettings from "./sections/AdminSettings";
import AdminServiceArea from "./sections/AdminServiceArea";
import AdminAbout from "./sections/AdminAbout";
import GalleryTab from "./sections/GalleryTab";
import ReviewsTab from "./sections/ReviewsTab";
import FaqTab from "./sections/FaqTab";
import PasswordTab from "./sections/PasswordTab";

const TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "anfragen", label: "Anfragen" },
  { id: "pakete", label: "Pakete & Preise" },
  { id: "extras", label: "Extras" },
  { id: "texte", label: "Texte" },
  { id: "einstellungen", label: "Einstellungen" },
  { id: "einsatzgebiet", label: "Einsatzgebiet" },
  { id: "ueber", label: "Über mich" },
  { id: "galerie", label: "Galerie" },
  { id: "bewertungen", label: "Bewertungen" },
  { id: "faq", label: "FAQ" },
  { id: "konto", label: "Passwort" },
];

const TAB_COMPONENTS = {
  dashboard: AdminDashboard,
  anfragen: AdminInquiries,
  pakete: PackagesTab,
  extras: AddonsTab,
  texte: TextsTab,
  einstellungen: AdminSettings,
  einsatzgebiet: AdminServiceArea,
  ueber: AdminAbout,
  galerie: GalleryTab,
  bewertungen: ReviewsTab,
  faq: FaqTab,
  konto: PasswordTab,
};

export default function AdminApp() {
  const [auth, setAuth] = useState(null);
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const loadedRef = useRef(false);

  const loadContent = useCallback(async () => {
    try {
      const d = await adminFetch("/content");
      setContent(mergeContent(d || {}));
      loadedRef.current = true;
    } catch {
      toast.error("Inhalte konnten nicht geladen werden.");
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setAuth(false);
        return;
      }
      try {
        await adminFetch("/admin/me");
        setAuth(true);
        await loadContent();
      } catch {
        setToken("");
        setAuth(false);
      }
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setToken(data.token);
      setAuth(true);
      await loadContent();
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await adminFetch("/admin/logout", { method: "POST" });
    } catch {}
    setToken("");
    setAuth(false);
    setEmail("");
    setPassword("");
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B] text-zinc-100">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-700 border-t-blue-500" />
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-100 flex items-center justify-center px-5">
        <div className="w-full max-w-sm rounded-2xl border border-[#27272A] bg-[#121215] p-8">
          <div className="flex items-center gap-3">
            <Logo className="h-10" />
            <div>
              <h1 className="font-display text-lg font-bold text-zinc-100">Adminbereich</h1>
              <p className="text-xs text-zinc-400">Wiese-Fahrzeugpflege</p>
            </div>
          </div>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-7 flex flex-col gap-4">
            {error ? (
              <p data-testid="admin-error-alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">
                {error}
              </p>
            ) : null}
            <div>
              <label className="block mb-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">E-Mail</label>
              <input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-3.5 h-11 text-sm text-zinc-100 outline-none focus:border-blue-500/60"
                placeholder="deine@email.de"
                autoComplete="username"
              />
            </div>
            <div>
              <label className="block mb-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">Passwort</label>
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-zinc-700 bg-zinc-900/60 px-3.5 h-11 text-sm text-zinc-100 outline-none focus:border-blue-500/60"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={busy}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-blue-600 text-sm font-semibold text-white hover:bg-blue-500 transition-colors disabled:opacity-60"
            >
              {busy ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  Anmelden …
                </>
              ) : (
                "Anmelden"
              )}
            </button>
          </form>
          <p className="mt-6 text-xs leading-relaxed text-zinc-400">
            Geschützter Bereich. Nach 5 Fehlversuchen wird die Anmeldung für 15 Minuten gesperrt.
          </p>
        </div>
      </div>
    );
  }

  const TabComp = TAB_COMPONENTS[tab];

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <header className="sticky top-0 z-40 h-14 border-b border-white/5 bg-[#0C0C0E]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl h-full px-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-8" />
            <span className="font-display text-sm font-bold text-zinc-100">Adminbereich</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              data-testid="admin-link-website"
              href="/"
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-4 text-xs font-semibold text-zinc-300 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
            >
              <Globe className="h-3.5 w-3.5" />
              Zur Website
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-rose-500/40 px-4 text-xs font-semibold text-rose-400 hover:bg-rose-500/10 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" />
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <nav data-testid="admin-tab-bar" className="sticky top-14 z-30 border-b border-white/5 bg-[#09090B]/95 backdrop-blur-xl">
        <div className="mx-auto max-w-5xl px-5 py-2 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`h-9 shrink-0 rounded-full px-4 text-sm font-semibold transition-colors ${
                tab === t.id
                  ? "bg-blue-600 text-white"
                  : "border border-zinc-800 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main data-testid="admin-content" className="mx-auto max-w-4xl px-5 py-8 pb-24">
        {content && TabComp ? (
          <TabComp
            content={content}
            reload={loadContent}
            setTab={setTab}
          />
        ) : null}
      </main>
    </div>
  );
}
